<template>
  <v-dialog v-model="dialog" max-width="600px" persistent>
    <v-card>
      <v-card-title class="headline grey lighten-2">
        {{ $t('colleges.edit_period') }}
      </v-card-title>

      <v-form ref="form" v-model="valid" lazy-validation>
        <v-card-text class="pt-6">
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="formData.name"
                  :label="$t('colleges.period_name')"
                  :rules="fieldRequired"
                  outlined
                  dense
                  :disabled="loading || periodEnded"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.startDate"
                  :label="$t('colleges.start_date')"
                  :rules="startDateRules"
                  type="date"
                  outlined
                  dense
                  :disabled="loading || periodEnded || periodStarted"
                  :hint="periodStarted ? $t('colleges.start_date_locked_hint') : ''"
                  :persistent-hint="periodStarted"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.endDate"
                  :label="$t('colleges.end_date')"
                  :rules="endDateRules"
                  type="date"
                  outlined
                  dense
                  :disabled="loading || periodEnded"
                />
              </v-col>

              <v-col v-if="periodEnded" cols="12">
                <v-alert type="warning" dense outlined>
                  {{ $t('colleges.period_ended_alert') }}
                </v-alert>
              </v-col>

              <v-col v-else-if="periodStarted" cols="12">
                <v-alert type="info" dense outlined>
                  {{ $t('colleges.editing_started_period_info') }}
                </v-alert>
              </v-col>

              <v-col v-if="isDefaultPeriod" cols="12">
                <v-alert type="info" dense outlined>
                  {{ $t('colleges.editing_default_period_info') }}
                </v-alert>
              </v-col>

              <v-col v-if="validationMessage" cols="12">
                <v-alert type="warning" dense>
                  {{ $t(`colleges.${validationMessage}`, validationParams) }}
                </v-alert>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            color="grey darken-1"
            :disabled="loading"
            @click="onCancel"
          >
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="loading"
            :disabled="!valid || periodEnded"
            @click="onSave"
          >
            {{ $t('common.to_save') }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import LoadingMixin from '@/mixins/loading';
import SnackbarMixin from '@/mixins/snackbar';
import academicPeriodService from '@/services/academicPeriod.service';
import {
  validateStartBeforeEnd,
  validateByCalendarTypeBoundaries,
  hasPeriodStarted,
  hasPeriodEnded,
  validateNoOverlap,
} from '@/utils/academicPeriodValidations';
import DateUtils from '@/utils/date.utils';

export default {
  name: 'EditAcademicPeriodModal',
  mixins: [LoadingMixin, SnackbarMixin],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    period: {
      type: Object,
      default: null,
    },
    calendarType: {
      type: Object,
      default: null,
    },
    periods: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    valid: true,
    formData: {
      name: '',
      startDate: '',
      endDate: '',
    },
    validationMessage: '',
    validationParams: {},
    dateValidationEnabled: false,
  }),
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    isDefaultPeriod() {
      return this.period && this.period.calendarTypeId !== null;
    },
    periodStarted() {
      return !!this.period && hasPeriodStarted(this.period.startDate);
    },
    periodEnded() {
      return !!this.period && hasPeriodEnded(this.period.endDate);
    },
    originalEndDate() {
      return this.period
        ? DateUtils.normalizeDateToYYYYMMDD(this.period.endDate)
        : null;
    },
    fieldRequired() {
      return [(v) => !!v || this.$t('common.field_required')];
    },
    startDateRules() {
      return [
        (v) => !!v || this.$t('common.field_required'),
        (v) => this.validateDates(v, this.formData.endDate, 'start'),
      ];
    },
    endDateRules() {
      return [
        (v) => !!v || this.$t('common.field_required'),
        (v) => this.validateDates(this.formData.startDate, v, 'end'),
      ];
    },
  },
  watch: {
    period: {
      handler(newVal) {
        if (newVal) {
          this.formData = {
            name: newVal.name || '',
            startDate: DateUtils.normalizeDateToYYYYMMDD(newVal.startDate) || '',
            endDate: DateUtils.normalizeDateToYYYYMMDD(newVal.endDate) || '',
          };
          this.resetDateValidationState(false);
          this.applyDateValidationMessage();
        }
      },
      immediate: true,
      deep: true,
    },
    dialog(newVal) {
      if (newVal && this.period) {
        this.formData = {
          name: this.period.name || '',
          startDate: DateUtils.normalizeDateToYYYYMMDD(this.period.startDate) || '',
          endDate: DateUtils.normalizeDateToYYYYMMDD(this.period.endDate) || '',
        };
        this.resetDateValidationState(false);
        this.applyDateValidationMessage();
      } else if (!newVal) {
        this.resetDateValidationState();
      }
    },
  },
  methods: {
    resetDateValidationState(clearMessage = true) {
      this.dateValidationEnabled = false;
      if (clearMessage) {
        this.validationMessage = '';
        this.validationParams = {};
      }
    },
    applyDateValidationMessage(
      startDate = this.formData.startDate,
      endDate = this.formData.endDate,
    ) {
      const validation = this.runDateValidation(
        startDate,
        endDate,
      );
      this.validationMessage = validation.isValid ? '' : validation.message || '';
      this.validationParams = validation.isValid ? {} : validation.params || {};
      return validation;
    },
    runDateValidation(startDate, endDate) {
      if (!startDate || !endDate) {
        return { isValid: true };
      }

      const basicValidation = validateStartBeforeEnd(startDate, endDate);
      if (!basicValidation.isValid) {
        return basicValidation;
      }

      // A period that already started can only be extended: its end date may
      // move forward (that is how diagnostics get reactivated) but never back.
      // The calendar type window is deliberately skipped here — it exists to
      // keep new periods well formed, and an extension is an operational
      // override that routinely pushes the end date past that window.
      if (this.periodStarted) {
        if (this.originalEndDate && endDate < this.originalEndDate) {
          return { isValid: false, message: 'validation_started_period_end' };
        }

        return validateNoOverlap({ startDate, endDate }, this.periods, this.period.id);
      }

      if (this.isDefaultPeriod && this.calendarType) {
        const typeValidation = validateByCalendarTypeBoundaries(
          this.calendarType,
          startDate,
          endDate,
        );
        if (!typeValidation.isValid) {
          return typeValidation;
        }
      }

      return validateNoOverlap({ startDate, endDate }, this.periods, this.period.id);
    },
    shouldShowFieldError(message, field) {
      if (!message || !field) {
        return true;
      }

      if (message === 'validation_start_before_end' || message === 'validation_overlap') {
        return true;
      }

      if (field === 'start') {
        return message.endsWith('_start');
      }

      if (field === 'end') {
        return message.endsWith('_end');
      }

      return true;
    },
    validateDates(startDate, endDate, field) {
      const validation = this.applyDateValidationMessage(startDate, endDate);

      if (!this.dateValidationEnabled) {
        return true;
      }

      if (!validation.isValid && this.shouldShowFieldError(validation.message, field)) {
        return this.$t(`colleges.${validation.message}`, validation.params);
      }

      return true;
    },
    onCancel() {
      this.dialog = false;
      this.$refs.form.reset();
      this.resetDateValidationState();
    },
    async onSave() {
      this.dateValidationEnabled = true;
      if (!this.$refs.form.validate()) {
        return;
      }

      try {
        this.loader();
        const payload = {
          name: this.formData.name,
          startDate: DateUtils.normalizeDateToYYYYMMDD(this.formData.startDate) || '',
          endDate: DateUtils.normalizeDateToYYYYMMDD(this.formData.endDate) || '',
        };
        const response = await academicPeriodService.update(
          this.period.id,
          payload,
        );

        if (response.status === 200) {
          this.snackbarSuccess(this.$t('colleges.period_updated_success'));
          this.$emit('period-updated', response.data);
          this.dialog = false;
          this.$refs.form.reset();
          this.resetDateValidationState();
        }
      } catch (error) {
        this.snackbarError(this.$t('colleges.period_error'));
      } finally {
        this.loader();
      }
    },
  },
};
</script>
